import { Fragment } from "react";
import { pillifyRichText } from "@/lib/richTextHelpers";
import { slotBorderStyle } from "@/lib/borderHelpers";
import DOMPurify from "dompurify";

/**
 * Per-element-type render components. Extracted from ElementRenderer.jsx so
 * each case is a small focused unit. All renderers receive:
 *   - p:       element.props
 *   - spacing: precomputed style (margins, padding, outer border, inner border)
 *
 * Recursive layout renderers (column/verticalLayout) additionally receive an
 * `ElementContent` reference so they can render nested children without an
 * import cycle with ElementRenderer.jsx.
 */

// ── Shared helpers ──────────────────────────────────────────────────────────

export function EjsTag({ binding, small }) {
  return (
    <span className="ejs-tag" style={{ fontSize: small ? 8 : 10 }}>
      {"<%= "}{binding}{" %>"}
    </span>
  );
}

/**
 * Build cell border style for a table cell, honoring:
 *  - per-column borderLeft / borderRight toggles
 *  - allCellBorders preset (all four sides)
 *  - existing bottom-only behavior as the default
 */
export function buildCellBorders({ col, color, allCellBorders, includeBottom = true }) {
  const w = "1px";
  const style = {};
  if (col?.borderLeft || allCellBorders)  style.borderLeft  = `${w} solid ${color}`;
  if (col?.borderRight || allCellBorders) style.borderRight = `${w} solid ${color}`;
  if (allCellBorders) {
    style.borderTop = `${w} solid ${color}`;
    style.borderBottom = `${w} solid ${color}`;
  } else if (includeBottom) {
    style.borderBottom = `${w} solid ${color}`;
  }
  return style;
}

/**
 * DOMPurify config for canvas rich-text rendering. The rich text comes from a
 * TipTap editor inside the designer (authored by the same user), so this is
 * defense-in-depth — strips anything that could execute script or load external
 * URLs. `data-ejs-path` is in ADD_ATTR so legacy pill-marker spans survive.
 */
const RICH_TEXT_PURIFY_CONFIG = {
  ADD_ATTR: ["data-ejs-path"],
  FORBID_TAGS: ["script", "iframe", "object", "embed", "form", "input", "style"],
  FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus", "onblur"],
};

function sanitizeRichText(html) {
  return DOMPurify.sanitize(html, RICH_TEXT_PURIFY_CONFIG);
}

// ── Text-family renderers ───────────────────────────────────────────────────

export function HeadingRender({ p, spacing }) {
  return (
    <div style={spacing}>
      <h2
        style={{
          fontSize: p.fontSize,
          fontWeight: p.fontWeight,
          fontFamily: p.fontFamily,
          textAlign: p.textAlign,
          color: p.color,
          letterSpacing: p.letterSpacing,
          textTransform: p.textTransform,
          margin: 0,
        }}
      >
        {p.ejsBinding ? <EjsTag binding={p.ejsBinding} /> : p.content}
      </h2>
    </div>
  );
}

export function TextRender({ p, spacing }) {
  if (p.richText) {
    const styledHtml = sanitizeRichText(pillifyRichText(p.richContent || ""));
    return (
      <div
        style={spacing}
        className="rich-text-render"
        dangerouslySetInnerHTML={{ __html: styledHtml }}
      />
    );
  }
  return (
    <div style={spacing}>
      <p
        style={{
          fontSize: p.fontSize,
          fontWeight: p.fontWeight,
          fontFamily: p.fontFamily,
          fontStyle: p.fontStyle,
          textAlign: p.textAlign,
          color: p.color,
          lineHeight: p.lineHeight,
          margin: 0,
          whiteSpace: "pre-wrap",
        }}
      >
        {p.ejsBinding ? <EjsTag binding={p.ejsBinding} /> : p.content}
      </p>
    </div>
  );
}

export function FooterRender({ p, spacing }) {
  return (
    <div style={spacing}>
      <p style={{ fontSize: p.fontSize, fontFamily: p.fontFamily, textAlign: p.textAlign, color: p.color, margin: 0 }}>
        {p.ejsBinding ? <EjsTag binding={p.ejsBinding} /> : p.content}
      </p>
    </div>
  );
}

export function PaymentTermsRender({ p, spacing }) {
  return (
    <div style={spacing}>
      <p style={{ fontSize: p.fontSize, color: p.color, lineHeight: p.lineHeight, margin: 0, fontFamily: p.fontFamily }}>
        {p.content}
      </p>
    </div>
  );
}

export function PageNumberRender({ p, spacing }) {
  return (
    <div style={spacing}>
      <p style={{ fontSize: p.fontSize, fontFamily: p.fontFamily, textAlign: p.textAlign, color: p.color, margin: 0 }}>
        {(p.format || "Page {current} of {total}").replace("{current}", "1").replace("{total}", "3")}
      </p>
    </div>
  );
}

// ── Image-family renderers ──────────────────────────────────────────────────

export function CompanyLogoRender({ p, spacing }) {
  return (
    <div style={{ ...spacing, textAlign: p.alignment }}>
      {p.src ? (
        <img src={p.src} alt="Logo" style={{ width: p.width, height: p.height, objectFit: "contain" }} />
      ) : (
        <div style={{ width: p.width, height: p.height, backgroundColor: "#F8FAFC", display: "inline-flex", alignItems: "center", justifyContent: "center", border: "1.5px dashed #CBD5E1", borderRadius: 4 }}>
          <span style={{ color: "#94A3B8", fontSize: 11 }}>
            {p.ejsBinding ? <EjsTag binding={p.ejsBinding} /> : "Company Logo"}
          </span>
        </div>
      )}
    </div>
  );
}

export function ImageRender({ p, spacing }) {
  return (
    <div style={{ ...spacing, textAlign: p.alignment }}>
      {p.src ? (
        <img src={p.src} alt="" style={{ width: p.width, height: p.height, objectFit: "contain", borderRadius: p.borderRadius }} />
      ) : (
        <div style={{ width: p.width, height: p.height, backgroundColor: "#F8FAFC", display: "inline-flex", alignItems: "center", justifyContent: "center", border: "1.5px dashed #CBD5E1", borderRadius: p.borderRadius || 4 }}>
          <span style={{ color: "#94A3B8", fontSize: 11 }}>
            {p.ejsBinding ? <EjsTag binding={p.ejsBinding} /> : "Image"}
          </span>
        </div>
      )}
    </div>
  );
}

export function SignatureRender({ p, spacing }) {
  return (
    <div style={{ ...spacing, textAlign: p.alignment }}>
      <div style={{ display: "inline-block" }}>
        <div style={{ borderBottom: `1px solid ${p.lineColor}`, width: p.lineWidth, marginBottom: 4, marginTop: 30 }} />
        <p style={{ fontSize: p.fontSize, color: p.color, margin: 0, fontFamily: p.fontFamily }}>{p.label}</p>
      </div>
    </div>
  );
}

export function QrCodeRender({ p, spacing }) {
  return (
    <div style={{ ...spacing, textAlign: p.alignment }}>
      <div style={{ width: p.size, height: p.size, border: "2px dashed #CBD5E1", borderRadius: 4, display: "inline-flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 4, backgroundColor: "#FAFBFC" }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="1.5">
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="3" height="3" />
          <rect x="18" y="14" width="3" height="3" />
          <rect x="14" y="18" width="3" height="3" />
          <rect x="18" y="18" width="3" height="3" />
        </svg>
        <span style={{ fontSize: 9, color: "#94A3B8" }}>QR Code</span>
      </div>
    </div>
  );
}

export function BarcodeRender({ p, spacing }) {
  return (
    <div style={{ ...spacing, textAlign: p.alignment }}>
      <div style={{ width: p.width, height: p.height, border: "2px dashed #CBD5E1", borderRadius: 4, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4, backgroundColor: "#FAFBFC" }}>
        <span style={{ fontSize: 11, color: "#94A3B8", fontFamily: "monospace", letterSpacing: 2 }}>
          ||||| Barcode |||||
        </span>
      </div>
    </div>
  );
}

// ── Info-block renderers ────────────────────────────────────────────────────

export function InvoiceDetailsRender({ p, spacing }) {
  return (
    <div style={{ ...spacing, textAlign: p.alignment }}>
      <table data-x-excluded="true"
        style={{ fontSize: p.fontSize, fontFamily: p.fontFamily, borderCollapse: "collapse", marginLeft: p.alignment === "right" ? "auto" : 0 }}>
        <tbody data-x-excluded="true">
          {(p.fields || []).map((field, i) => (
            <tr data-x-excluded="true" key={i}>
              <td style={{ color: p.labelColor, paddingRight: 12, paddingTop: 2, paddingBottom: 2, textAlign: p.labelAlign || "left", width: p.labelWidth }}>
                {field.label}:
              </td>
              <td style={{ color: p.valueColor, fontWeight: 500, paddingTop: 2, paddingBottom: 2, textAlign: p.valueAlign || "right" }}>
                {field.ejsBinding ? <EjsTag binding={field.ejsBinding} /> : field.value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function CustomerInfoRender({ p, spacing }) {
  return (
    <div style={spacing}>
      {p.title && (
        <p style={{ fontSize: p.titleFontSize, fontWeight: p.titleFontWeight, color: p.titleColor, margin: "0 0 4px 0", fontFamily: p.fontFamily }}>
          {p.title}
        </p>
      )}
      {(p.fields || []).map((field, i) => (
        <p key={i} style={{ fontSize: p.fontSize, color: p.color, margin: "2px 0", fontFamily: p.fontFamily }}>
          {field.label && <strong>{field.label}: </strong>}
          {field.ejsBinding ? <EjsTag binding={field.ejsBinding} /> : field.value}
        </p>
      ))}
    </div>
  );
}

export function TotalsRender({ p, spacing }) {
  return (
    <div style={{ ...spacing, textAlign: p.alignment }}>
      <table data-x-excluded="true"
        style={{ fontSize: p.fontSize, fontFamily: p.fontFamily, borderCollapse: "collapse", marginLeft: p.alignment === "right" ? "auto" : 0, width: p.width }}>
        <tbody data-x-excluded="true">
          {(p.rows || []).map((row, i) => (
            <tr data-x-excluded="true" key={i}>
              <td style={{ color: p.labelColor, paddingRight: 20, paddingTop: 4, paddingBottom: 4, fontWeight: row.fontWeight, textAlign: p.labelAlign || "left" }}>
                {row.label}:
              </td>
              <td style={{ color: p.valueColor, textAlign: p.valueAlign || "right", paddingTop: 4, paddingBottom: 4, fontWeight: row.fontWeight }}>
                {row.ejsBinding ? <EjsTag binding={row.ejsBinding} /> : row.value || "0.00"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Big table renderers (line items + summary table) ────────────────────────

function buildLineItemsHeaderRows(cols, groups, hasGroups, coverage, p) {
  const headerRow = (
    <tr data-x-excluded="true">
      {cols.map((col, i) => (
        <th key={i} style={{ backgroundColor: p.headerBgColor, color: p.headerTextColor, fontWeight: p.headerFontWeight, padding: p.cellPadding, textAlign: col.align || "left", width: col.width, ...buildCellBorders({ col, color: p.borderColor, allCellBorders: p.allCellBorders }) }}>
          {col.header}
        </th>
      ))}
    </tr>
  );

  const groupHeaderRow = hasGroups ? (
    <tr data-x-excluded="true">
      {cols.map((col, i) => {
        const g = groups.find((g2) => g2.startCol === i);
        if (g) return <th key={i} colSpan={g.span} style={{ backgroundColor: p.headerBgColor, color: p.headerTextColor, fontWeight: p.headerFontWeight, padding: `${Math.floor(p.cellPadding / 2)}px ${p.cellPadding}px`, textAlign: "center", ...buildCellBorders({ col, color: p.borderColor, allCellBorders: p.allCellBorders }) }}>{g.label}</th>;
        const inGroup = coverage[i] !== null && groups.find((g2) => g2.startCol !== i && i >= g2.startCol && i < g2.startCol + g2.span);
        if (inGroup) return null;
        return <th key={i} rowSpan={2} style={{ backgroundColor: p.headerBgColor, color: p.headerTextColor, fontWeight: p.headerFontWeight, padding: p.cellPadding, textAlign: col.align || "left", width: col.width, verticalAlign: "middle", ...buildCellBorders({ col, color: p.borderColor, allCellBorders: p.allCellBorders }) }}>{col.header}</th>;
      })}
    </tr>
  ) : null;

  const subHeaderRow = hasGroups ? (
    <tr data-x-excluded="true">
      {cols.map((col, i) => {
        const inGroup = coverage[i] !== null;
        if (!inGroup) return null;
        return <th key={i} style={{ backgroundColor: p.headerBgColor, color: p.headerTextColor, fontWeight: p.headerFontWeight, padding: `${Math.floor(p.cellPadding / 2)}px ${p.cellPadding}px`, textAlign: col.align || "left", width: col.width, ...buildCellBorders({ col, color: p.borderColor, allCellBorders: p.allCellBorders }) }}>{col.header}</th>;
      })}
    </tr>
  ) : null;

  return { headerRow, groupHeaderRow, subHeaderRow };
}

export function LineItemsRender({ p, spacing }) {
  const cols = p.columns || [];
  const subs = p.subRows || [];
  const groups = p.columnGroups || [];
  const hasSubs = subs.length > 0;
  const hasGroups = groups.length > 0;

  const coverage = new Array(cols.length).fill(null);
  groups.forEach((g) => {
    for (let i = g.startCol; i < g.startCol + g.span && i < cols.length; i++) coverage[i] = g;
  });

  const { headerRow, groupHeaderRow, subHeaderRow } = buildLineItemsHeaderRows(cols, groups, hasGroups, coverage, p);

  return (
    <div style={spacing}>
      <table data-x-excluded="true" style={{ width: "100%", tableLayout: "fixed", fontSize: p.fontSize, fontFamily: p.fontFamily, borderCollapse: "collapse" }}>
        <thead data-x-excluded="true">
          {hasGroups ? <>{groupHeaderRow}{subHeaderRow}</> : headerRow}
        </thead>
        <tbody data-x-excluded="true">
          {Array.from({ length: p.sampleRows || 3 }).map((_, rowIdx) => {
            const stripeBg = p.stripedRows && rowIdx % 2 === 1 ? p.stripedColor : "transparent";
            return (
              <Fragment key={rowIdx}>
                <tr data-x-excluded="true" style={{ backgroundColor: stripeBg }}>
                  {cols.map((col, colIdx) => (
                    <td key={colIdx} style={{ padding: p.cellPadding, textAlign: col.align || "left", wordBreak: "break-word", overflowWrap: "break-word", ...buildCellBorders({ col, color: p.borderColor, allCellBorders: p.allCellBorders, includeBottom: !hasSubs }) }}>
                      {col.key === "index" ? rowIdx + 1 : <span className="text-gray-400 italic text-[10px]">{col.key}</span>}
                    </td>
                  ))}
                </tr>
                {subs.map((sr, srIdx) => {
                  const startCol = Math.min(sr.startCol || 0, cols.length - 1);
                  const colSpan = Math.min(sr.colSpan || cols.length, cols.length - startCol);
                  const afterCols = cols.length - startCol - colSpan;
                  const isLast = srIdx === subs.length - 1;
                  return (
                    <tr data-x-excluded="true" key={`sub-${rowIdx}-${srIdx}`} style={{ backgroundColor: stripeBg }}>
                      {startCol > 0 && <td colSpan={startCol} style={{ padding: `${Math.floor(p.cellPadding / 2)}px ${p.cellPadding}px`, borderBottom: isLast ? `1px solid ${p.borderColor}` : "none" }} />}
                      <td colSpan={colSpan} style={{ padding: `${Math.floor(p.cellPadding / 2)}px ${p.cellPadding}px`, fontSize: sr.fontSize || p.fontSize, color: sr.color || "#6B7280", fontStyle: sr.fontStyle || "italic", textAlign: sr.align || "left", borderBottom: isLast ? `1px solid ${p.borderColor}` : "none" }}>
                        {sr.label ? `${sr.label}: ` : ""}<span className="text-gray-400 italic text-[10px]">{sr.key || "field"}</span>
                      </td>
                      {afterCols > 0 && <td colSpan={afterCols} style={{ padding: `${Math.floor(p.cellPadding / 2)}px ${p.cellPadding}px`, borderBottom: isLast ? `1px solid ${p.borderColor}` : "none" }} />}
                    </tr>
                  );
                })}
              </Fragment>
            );
          })}
        </tbody>
        {p.showTotalRow && (
          <tfoot data-x-excluded="true">
            <tr data-x-excluded="true">
              {cols.map((col, i) => (
                <td key={i} style={{ padding: p.cellPadding, textAlign: col.align || "left", borderTop: `2px solid ${p.borderColor}`, fontWeight: "bold", fontSize: p.fontSize, ...(p.allCellBorders || col.borderLeft || col.borderRight ? buildCellBorders({ col, color: p.borderColor, allCellBorders: p.allCellBorders, includeBottom: p.allCellBorders }) : {}) }}>
                  {i === 0 ? (p.totalLabel == null ? "Total" : p.totalLabel) : col.showTotal ? <span className="text-gray-500 italic text-[10px]">Σ {col.key}</span> : ""}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

export function SummaryTableRender({ p, spacing }) {
  const cols = p.columns || [];
  const groups = p.columnGroups || [];
  const hasGroups = groups.length > 0;
  const coverage = new Array(cols.length).fill(null);
  groups.forEach((g) => {
    for (let i = g.startCol; i < g.startCol + g.span && i < cols.length; i++) coverage[i] = g;
  });

  const flatHeader = (
    <tr data-x-excluded="true">
      {cols.map((col, i) => (
        <th key={i} style={{ backgroundColor: p.headerBgColor, color: p.headerTextColor, fontWeight: p.headerFontWeight, padding: p.cellPadding, textAlign: col.align || "left", width: col.width, ...buildCellBorders({ col, color: p.borderColor, allCellBorders: p.allCellBorders }) }}>
          {col.header}
        </th>
      ))}
    </tr>
  );
  const groupRow = hasGroups ? (
    <tr data-x-excluded="true">
      {cols.map((col, i) => {
        const g = groups.find((g2) => g2.startCol === i);
        if (g) return <th key={i} colSpan={g.span} style={{ backgroundColor: p.headerBgColor, color: p.headerTextColor, fontWeight: p.headerFontWeight, padding: `${Math.floor(p.cellPadding / 2)}px ${p.cellPadding}px`, textAlign: "center", ...buildCellBorders({ col, color: p.borderColor, allCellBorders: p.allCellBorders }) }}>{g.label}</th>;
        const inGroup = coverage[i] !== null && groups.find((g2) => g2.startCol !== i && i >= g2.startCol && i < g2.startCol + g2.span);
        if (inGroup) return null;
        return <th key={i} rowSpan={2} style={{ backgroundColor: p.headerBgColor, color: p.headerTextColor, fontWeight: p.headerFontWeight, padding: p.cellPadding, textAlign: col.align || "left", width: col.width, verticalAlign: "middle", ...buildCellBorders({ col, color: p.borderColor, allCellBorders: p.allCellBorders }) }}>{col.header}</th>;
      })}
    </tr>
  ) : null;
  const subRow = hasGroups ? (
    <tr data-x-excluded="true">
      {cols.map((col, i) => {
        const inGroup = coverage[i] !== null;
        if (!inGroup) return null;
        return <th key={i} style={{ backgroundColor: p.headerBgColor, color: p.headerTextColor, fontWeight: p.headerFontWeight, padding: `${Math.floor(p.cellPadding / 2)}px ${p.cellPadding}px`, textAlign: col.align || "left", width: col.width, ...buildCellBorders({ col, color: p.borderColor, allCellBorders: p.allCellBorders }) }}>{col.header}</th>;
      })}
    </tr>
  ) : null;

  return (
    <div style={spacing}>
      <table data-x-excluded="true" style={{ width: "100%", tableLayout: "fixed", fontSize: p.fontSize, fontFamily: p.fontFamily, borderCollapse: "collapse" }}>
        <thead data-x-excluded="true">
          {hasGroups ? <>{groupRow}{subRow}</> : flatHeader}
        </thead>
        <tbody data-x-excluded="true">
          {Array.from({ length: p.sampleRows || 3 }).map((_, rowIdx) => {
            const stripeBg = p.stripedRows && rowIdx % 2 === 1 ? p.stripedColor : "transparent";
            return (
              <tr data-x-excluded="true" key={rowIdx} style={{ backgroundColor: stripeBg }}>
                {cols.map((col, colIdx) => (
                  <td key={colIdx} style={{ padding: p.cellPadding, textAlign: col.align || "left", wordBreak: "break-word", overflowWrap: "break-word", ...buildCellBorders({ col, color: p.borderColor, allCellBorders: p.allCellBorders }) }}>
                    <span className="text-gray-400 italic text-[10px]">{col.key}</span>
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
        {p.showTotalRow && (
          <tfoot data-x-excluded="true">
            <tr data-x-excluded="true">
              {cols.map((col, i) => (
                <td key={i} style={{ padding: p.cellPadding, textAlign: col.align || "left", borderTop: `2px solid ${p.borderColor}`, fontWeight: "bold", fontSize: p.fontSize, ...(p.allCellBorders || col.borderLeft || col.borderRight ? buildCellBorders({ col, color: p.borderColor, allCellBorders: p.allCellBorders, includeBottom: p.allCellBorders }) : {}) }}>
                  {i === 0 ? (p.totalLabel == null ? "Total" : p.totalLabel) : col.showTotal ? <span className="text-gray-500 italic text-[10px]">Σ {col.key}</span> : ""}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

// ── List renderers (Key-Value list, Tax Details, Custom) ────────────────────

function KeyValueListDualRow({ p, it, i, rowMargin }) {
  const renderVal = (side) => side?.ejsBinding
    ? <EjsTag binding={side.ejsBinding} />
    : (side?.defaultValue || <span className="text-gray-300 italic">empty</span>);
  return (
    <div key={it.id || i} style={{ display: "flex", width: "100%", ...rowMargin, boxSizing: "border-box" }}>
      <div style={{ width: "50%", display: "flex", alignItems: "center", boxSizing: "border-box", paddingRight: 8 }}>
        <div style={{ width: p.labelWidth || "40%", textAlign: p.labelAlign, fontWeight: 600, color: p.labelColor }}>{it.left?.label}</div>
        <div style={{ flex: 1, textAlign: p.valueAlign, color: p.valueColor, wordBreak: "break-word" }}>{renderVal(it.left)}</div>
      </div>
      <div style={{ width: "50%", display: "flex", alignItems: "center", boxSizing: "border-box", paddingLeft: 8 }}>
        <div style={{ width: p.labelWidth || "40%", textAlign: p.labelAlign, fontWeight: 600, color: p.labelColor }}>{it.right?.label}</div>
        <div style={{ flex: 1, textAlign: p.valueAlign, color: p.valueColor, wordBreak: "break-word" }}>{renderVal(it.right)}</div>
      </div>
    </div>
  );
}

export function KeyValueListRender({ p, spacing }) {
  const items = p.items || [];
  const vGap = p.verticalGap == null ? 4 : p.verticalGap;
  const rowMargin = { marginTop: vGap, marginBottom: vGap };
  return (
    <div style={{ ...spacing, fontSize: p.fontSize, fontFamily: p.fontFamily, color: p.color, width: "100%", boxSizing: "border-box" }}>
      {p.componentHeading ? (
        <div style={{ fontWeight: "bold", fontSize: (p.fontSize || 11) + 1, color: p.color, marginBottom: 4, width: "100%" }}>
          {p.componentHeading}
        </div>
      ) : null}
      {items.map((it, i) => {
        const layoutPattern = it.layoutPattern || "single";
        if (layoutPattern === "dual") {
          return <KeyValueListDualRow key={it.id || i} p={p} it={it} i={i} rowMargin={rowMargin} />;
        }
        const rowType = it.rowType || "label_value";
        if (rowType === "value_only") {
          const rowFs = it.rowFontSize ? Number(it.rowFontSize) : undefined;
          return (
            <p
              key={it.id || i}
              style={{
                fontSize: rowFs || p.fontSize,
                fontWeight: it.rowIsBold ? "bold" : "normal",
                color: p.valueColor || p.color,
                ...rowMargin,
                textAlign: p.valueAlign || "left",
                width: "100%",
                wordBreak: "break-word",
                boxSizing: "border-box",
              }}
            >
              {it.ejsBinding ? <EjsTag binding={it.ejsBinding} /> : (it.staticText || <span className="text-gray-300 italic">empty</span>)}
            </p>
          );
        }
        return (
          <div key={it.id || i} style={{ display: "flex", gap: p.gap || 4, ...rowMargin, lineHeight: 1.4, width: "100%", boxSizing: "border-box" }}>
            <div style={{ width: p.labelWidth || "40%", color: p.labelColor, textAlign: p.labelAlign, fontWeight: 600, wordBreak: "break-word" }}>{it.label}</div>
            <div style={{ flex: 1, color: p.valueColor, textAlign: p.valueAlign, wordBreak: "break-word" }}>
              {it.ejsBinding ? <EjsTag binding={it.ejsBinding} /> : (it.defaultValue || "")}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function TaxDetailsRender({ p, spacing }) {
  const preRows = p.preLoopItems || [];
  const postRows = p.postLoopItems || [];
  const sampleRows = p.sampleRows || 2;
  const divider = `${p.sectionDividerWidth || 1}px solid ${p.sectionDividerColor || "#E5E7EB"}`;
  const rowStyle = { display: "flex", gap: p.gap || 4, padding: `${p.rowPadding || 4}px 0`, lineHeight: 1.4, width: "100%", boxSizing: "border-box" };
  return (
    <div style={{ ...spacing, fontSize: p.fontSize, fontFamily: p.fontFamily, color: p.color, width: "100%", boxSizing: "border-box" }}>
      {preRows.map((it, i) => (
        <div key={`pre-${it.id || i}`} style={{ ...rowStyle, fontWeight: it.isBold ? "bold" : "normal" }}>
          <div style={{ width: p.labelWidth, color: p.labelColor, textAlign: p.labelAlign, wordBreak: "break-word" }}>{it.label}</div>
          <div style={{ flex: 1, color: p.valueColor, textAlign: p.valueAlign, wordBreak: "break-word" }}>
            {it.ejsBinding ? <EjsTag binding={it.ejsBinding} /> : ""}
          </div>
        </div>
      ))}
      {preRows.length > 0 && <div style={{ borderTop: divider, margin: "2px 0" }} />}
      {Array.from({ length: sampleRows }).map((_, i) => (
        <div key={`loop-${i}`} style={rowStyle}>
          <div style={{ width: p.labelWidth, color: p.labelColor, textAlign: p.labelAlign, wordBreak: "break-word" }}>
            <EjsTag binding={`${p.loopVariable}[key]`} small />
          </div>
          <div style={{ flex: 1, color: p.valueColor, textAlign: p.valueAlign, wordBreak: "break-word" }}>
            <EjsTag binding={`${p.loopVariable}[value]`} small />
          </div>
        </div>
      ))}
      {postRows.length > 0 && <div style={{ borderTop: divider, margin: "2px 0" }} />}
      {postRows.map((it, i) => (
        <div key={`post-${it.id || i}`} style={{ ...rowStyle, fontWeight: it.isBold ? "bold" : "normal" }}>
          <div style={{ width: p.labelWidth, color: p.labelColor, textAlign: p.labelAlign, wordBreak: "break-word" }}>{it.label}</div>
          <div style={{ flex: 1, color: p.valueColor, textAlign: p.valueAlign, wordBreak: "break-word" }}>
            {it.ejsBinding ? <EjsTag binding={it.ejsBinding} /> : ""}
          </div>
        </div>
      ))}
    </div>
  );
}

function CustomFieldText({ p, f }) {
  return (
    <>
      {f.label && <strong>{f.label}: </strong>}
      {f.ejsBinding ? <EjsTag binding={f.ejsBinding} /> : (f.defaultValue || f.key)}
    </>
  );
}

export function CustomRender({ p, spacing }) {
  const fields = p.fields || [];
  const layout = p.layout || "vertical";
  return (
    <div style={spacing}>
      {p.name && (
        <p style={{ fontSize: p.titleFontSize || 13, fontWeight: p.titleFontWeight || "bold", color: p.titleColor || "#111827", margin: "0 0 4px 0", fontFamily: p.fontFamily }}>
          {p.name}
        </p>
      )}
      {layout === "vertical" && fields.map((f, i) => (
        <p key={i} style={{ fontSize: p.fontSize, color: p.color, margin: "2px 0", fontFamily: p.fontFamily }}>
          <CustomFieldText p={p} f={f} />
        </p>
      ))}
      {layout === "horizontal" && (
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          {fields.map((f, i) => (
            <span key={i} style={{ fontSize: p.fontSize, color: p.color, fontFamily: p.fontFamily }}>
              <CustomFieldText p={p} f={f} />
            </span>
          ))}
        </div>
      )}
      {layout === "grid" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px 16px" }}>
          {fields.map((f, i) => (
            <p key={i} style={{ fontSize: p.fontSize, color: p.color, margin: "2px 0", fontFamily: p.fontFamily }}>
              <CustomFieldText p={p} f={f} />
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Layout renderers (columns + verticalLayout) ─────────────────────────────
// Receive `ElementContent` as a prop so they can recurse without import cycles.

function alignFromVerticalAlign(v) {
  return { top: "flex-start", center: "center", bottom: "flex-end" }[v] || "flex-start";
}

export function TwoColumnRender({ p, spacing, ElementContent }) {
  const align = alignFromVerticalAlign(p.verticalAlign);
  const leftB = slotBorderStyle(p.leftColumn?.elementBorder);
  const rightB = slotBorderStyle(p.rightColumn?.elementBorder);
  return (
    <div style={{ ...spacing, display: "flex", gap: p.gap, alignItems: align }}>
      <div style={{ width: p.leftWidth, flexShrink: 0, boxSizing: "border-box", ...leftB }}>
        {p.leftColumn?.type && <ElementContent element={{ id: "col-left", type: p.leftColumn.type, props: p.leftColumn.props || {} }} />}
      </div>
      <div style={{ flex: 1, boxSizing: "border-box", ...rightB }}>
        {p.rightColumn?.type && <ElementContent element={{ id: "col-right", type: p.rightColumn.type, props: p.rightColumn.props || {} }} />}
      </div>
    </div>
  );
}

export function ThreeColumnRender({ p, spacing, ElementContent }) {
  const align = alignFromVerticalAlign(p.verticalAlign);
  const leftB = slotBorderStyle(p.leftColumn?.elementBorder);
  const midB = slotBorderStyle(p.midColumn?.elementBorder);
  const rightB = slotBorderStyle(p.rightColumn?.elementBorder);
  return (
    <div style={{ ...spacing, display: "flex", gap: p.gap, alignItems: align }}>
      <div style={{ width: p.leftWidth, flexShrink: 0, boxSizing: "border-box", ...leftB }}>
        {p.leftColumn?.type && <ElementContent element={{ id: "col-left", type: p.leftColumn.type, props: p.leftColumn.props || {} }} />}
      </div>
      <div style={{ width: p.midWidth, flexShrink: 0, boxSizing: "border-box", ...midB }}>
        {p.midColumn?.type && <ElementContent element={{ id: "col-mid", type: p.midColumn.type, props: p.midColumn.props || {} }} />}
      </div>
      <div style={{ flex: 1, boxSizing: "border-box", ...rightB }}>
        {p.rightColumn?.type && <ElementContent element={{ id: "col-right", type: p.rightColumn.type, props: p.rightColumn.props || {} }} />}
      </div>
    </div>
  );
}

export function FourColumnRender({ p, spacing, ElementContent }) {
  const align = alignFromVerticalAlign(p.verticalAlign);
  const b1 = slotBorderStyle(p.col1?.elementBorder);
  const b2 = slotBorderStyle(p.col2?.elementBorder);
  const b3 = slotBorderStyle(p.col3?.elementBorder);
  const b4 = slotBorderStyle(p.col4?.elementBorder);
  return (
    <div style={{ ...spacing, display: "flex", gap: p.gap, alignItems: align }}>
      <div style={{ width: p.col1Width, flexShrink: 0, boxSizing: "border-box", ...b1 }}>
        {p.col1?.type && <ElementContent element={{ id: "col1", type: p.col1.type, props: p.col1.props || {} }} />}
      </div>
      <div style={{ width: p.col2Width, flexShrink: 0, boxSizing: "border-box", ...b2 }}>
        {p.col2?.type && <ElementContent element={{ id: "col2", type: p.col2.type, props: p.col2.props || {} }} />}
      </div>
      <div style={{ width: p.col3Width, flexShrink: 0, boxSizing: "border-box", ...b3 }}>
        {p.col3?.type && <ElementContent element={{ id: "col3", type: p.col3.type, props: p.col3.props || {} }} />}
      </div>
      <div style={{ width: p.col4Width || "25%", flexShrink: 0, boxSizing: "border-box", ...b4 }}>
        {p.col4?.type && <ElementContent element={{ id: "col4", type: p.col4.type, props: p.col4.props || {} }} />}
      </div>
    </div>
  );
}

export function VerticalLayoutRender({ p, spacing, ElementContent }) {
  const rows = p.rows || [];
  return (
    <div style={{ ...spacing, display: "flex", flexDirection: "column", gap: p.gap || 0, width: "100%", boxSizing: "border-box" }}>
      {rows.map((row, i) => {
        const rb = slotBorderStyle(row.elementBorder);
        return (
          <div
            key={row.id || i}
            style={{ width: "100%", boxSizing: "border-box", ...rb }}
            data-testid={`vrow-canvas-${i}`}
          >
            {row.childType ? (
              <ElementContent element={{ id: `${row.id}-child`, type: row.childType, props: row.childProps || {} }} />
            ) : (
              <div className="border border-dashed border-emerald-200 rounded text-[10px] text-emerald-700 bg-emerald-50/40 italic py-1 px-2">
                Empty row — pick an element in the right panel
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Misc / decorative renderers ─────────────────────────────────────────────

export function DividerRender({ p, spacing }) {
  return (
    <div style={spacing}>
      <hr style={{ border: "none", borderTop: `${p.thickness}px ${p.style} ${p.color}`, margin: 0 }} />
    </div>
  );
}

export function SpacerRender({ p, spacing }) {
  return (
    <div style={{ ...spacing, height: p.height, position: "relative" }}>
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", border: "1px dashed #E2E8F0", borderRadius: 3, backgroundColor: "#FAFBFC" }}>
        <span style={{ fontSize: 9, color: "#CBD5E1" }}>{p.height}px</span>
      </div>
    </div>
  );
}

export function PageBreakRender({ p, spacing }) {
  return (
    <div style={{ ...spacing, position: "relative", height: 36 }}>
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center" }}>
        <div style={{ flex: 1, borderTop: "2px dashed #94A3B8" }} />
        <span style={{ padding: "0 12px", fontSize: 9, color: "#94A3B8", backgroundColor: "white", fontFamily: "monospace", letterSpacing: 1 }}>
          PAGE BREAK
        </span>
        <div style={{ flex: 1, borderTop: "2px dashed #94A3B8" }} />
      </div>
    </div>
  );
}

export function CarriedForwardRender({ p, spacing }) {
  return (
    <div style={{ ...spacing, textAlign: p.textAlign }}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: p.fontSize, fontFamily: p.fontFamily, fontWeight: p.fontWeight, color: p.color, borderTop: "1px solid #E5E7EB", paddingTop: 4 }}>
        <span>{p.label}:</span>
        <EjsTag binding="calculated" />
      </div>
    </div>
  );
}

export function BroughtForwardRender({ p, spacing }) {
  return (
    <div style={{ ...spacing, textAlign: p.textAlign }}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: p.fontSize, fontFamily: p.fontFamily, fontWeight: p.fontWeight, color: p.color, borderBottom: "1px solid #E5E7EB", paddingBottom: 4 }}>
        <span>{p.label}:</span>
        <EjsTag binding="calculated" />
      </div>
    </div>
  );
}
